/**
 * ChatPanel — right-side AI chat panel matching Figma "Rocky" design.
 *
 * Streaming approach:
 *   - Completed messages live in `messages` state (never re-rendered during stream)
 *   - The in-flight streaming text lives in separate `streamingText` / `streamingThought` states
 *   - SSE deltas accumulate in refs; a RAF loop flushes refs → state
 *     at ~60fps so React renders each frame with new characters
 *   - On stream complete, the final text is committed to `messages` and
 *     streaming states are cleared
 *
 * Thought display:
 *   - During streaming: agentThought renders directly in the bubble (same style
 *     as final response — seamless streaming feel). Once agentFinalResponse
 *     starts arriving, the thought collapses into a small toggle and the
 *     final response takes over the bubble.
 *   - In committed messages: thought is shown as a collapsible section above
 *     the final response.
 */

import { useTranslation } from 'react-i18next';
import SafeTrans from '../../i18n/SafeTrans';
import ModelSelect from './modelSelect/ModelSelect';
import { useModelChoice } from './modelSelect/useModelChoice';
import { useLocale } from '../../i18n/useLocale';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOpenWorkspaceFile } from '../../hooks/useOpenWorkspaceFile';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useUserProfile } from '../../context/UserProfileContext';
import { useReportMetadata } from '../../context/ReportMetadataContext';
import { useWorkspaceNav } from '../../context/WorkspaceNavContext';
import { useChatPanel } from '../../context/ChatPanelContext';
import { setAgentState } from '../../stores/agentStateStore';
import { useAgent, getAgentWelcome } from '../../context/AgentContext';
import {
  gatewayClient,
  UploadedFile,
} from '../../client/GatewayClient';
import { appConfig, OFFICE_UPLOAD_ENABLED, AGUI_AGENTS } from '../../config';
import { refreshAuth } from '../../context/AuthContext';
import { gatewayAuthHeaders } from '../../context/gatewayAuthHeaders';
import Avatar from '@amzn/awsui-chat-components/avatar';
import { Icon } from '@amzn/awsui-components-react';
import Link from '@amzn/awsui-components-react/polaris/link';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { useFilesDragging } from '@amzn/awsui-components-react/polaris/file-dropzone';
import MarkdownRenderer from './MarkdownRenderer';
import AgUiChatPanel from './agui/AgUiChatPanel';
import ChatHistory from './ChatHistory';
import ContextFileBanner from './ContextFileBanner';
import SkillCard from './SkillCard';
import PendingQueue, { PendingQuestion } from './PendingQueue';

const PENDING_QUEUE_MAX = 3;

// Single source of truth for which uploads the agent can actually consume.
// Images -> image_reader; pdf/doc/docx/xls/xlsx -> Bedrock native document
// block; csv/txt/md/html/json/xml/yaml -> plain text; ppt/pptx -> sandbox
// (python-pptx). Office docs (doc/docx/xls/xlsx) and PowerPoint are gated by
// OFFICE_UPLOAD_ENABLED. The <input accept> attribute only filters the picker,
// NOT drag-and-drop — so processFiles enforces this list at runtime for both.
const BASE_UPLOAD_EXTS = [
  '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.pdf', '.csv', '.txt', '.md', '.html', '.json', '.xml', '.yaml', '.yml',
];
const OFFICE_UPLOAD_EXTS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
const getAllowedUploadExts = () =>
  OFFICE_UPLOAD_ENABLED ? [...BASE_UPLOAD_EXTS, ...OFFICE_UPLOAD_EXTS] : BASE_UPLOAD_EXTS;
import {
  getSkillClient,
  ListEquipmentCommand,
} from '../../client/SkillClient';
import type { EquipmentItem, CatalogItem } from '../../client/SkillClient';
import { SessionMessage } from '../../client/GatewayClient';
import ChoiceGroup from '../uiComponents/ChoiceGroup';
import type { ChoiceOption } from '../uiComponents/schema';
import {
  attachFollowUpRow,
  isFollowUpInteractive,
  markFollowUpSelection,
  parseFollowUpsEvent,
  type FollowUps,
} from './followUps';
import { getDefaultSuggestedQuestions } from '../../utils/suggestedQuestions';
import './ChatPanel.css';

// ── Types ───────────────────────────────────────────────────────────────────

/** Metadata captured from a file_output SSE event */
interface FileOutputInfo {
  path: string;       // e.g. "output/test/test_20260501.md"
  filename: string;   // e.g. "test_20260501.md"
  s3Key?: string;     // e.g. "webbwu/rocky/output/test/test_20260501.md"
  contentType?: string;
  size?: number;
}

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant' | 'divider';
  content: string;
  thought?: string;
  timestamp: Date;
  /** Files generated by the agent during this response */
  fileOutputs?: FileOutputInfo[];
  /** Workspace context file name shown on first send */
  contextFileName?: string;
  /** Asset KB name shown once on the first user message after binding to an
   *  asset scope (or after rebinding to a different one). */
  assetScopeName?: string;
  /** UC KB name shown once on the first user message after binding to a UC scope
   *  (the UC parallel of assetScopeName). */
  ucScopeName?: string;
  /** Storyline KB name shown once on the first user message after binding to a
   *  storyline scope (the Storyline parallel of assetScopeName). */
  storylineScopeName?: string;
  /** End-to-end response time in seconds (from Gateway timing event) */
  elapsedS?: number;
  /** Follow-up suggestions for this answer (live `follow_ups` event or history row). */
  followUps?: FollowUps;
}

interface ChatPanelProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  /** Hide the fullscreen toggle button. Used by surfaces (e.g. IVT Hub) that
   *  always want the panel in side-by-side mode. Defaults to false. */
  hideFullscreen?: boolean;
  /** Agent name. Defaults to 'rocky' (legacy mode). Agents in AGUI_AGENTS
   *  are rendered via CopilotKit headless (AG-UI protocol). */
  agentName?: string;
}



// ── Attachment metadata parser (strips Gateway storage format) ──────────────

const ATTACHMENTS_BLOCK_REGEX = /\s*\[Attachments\]\s*([\s\S]*)$/;
const ATTACHMENT_ITEM_REGEX = /-\s*\[([^\]]+)\]/g;
const PAPERCLIP_REGEX = /\n*📎\s*(.+)$/;

function parseUserContent(content: string): { text: string; attachedFiles: string[] } {
  const attachedFiles: string[] = [];

  let text = content;
  const blockMatch = text.match(ATTACHMENTS_BLOCK_REGEX);
  if (blockMatch) {
    const block = blockMatch[1];
    let itemMatch;
    while ((itemMatch = ATTACHMENT_ITEM_REGEX.exec(block)) !== null) {
      attachedFiles.push(itemMatch[1]);
    }
    ATTACHMENT_ITEM_REGEX.lastIndex = 0;
    text = text.replace(ATTACHMENTS_BLOCK_REGEX, '').trim();
  }

  const clipMatch = text.match(PAPERCLIP_REGEX);
  if (clipMatch) {
    clipMatch[1].split(',').forEach(f => attachedFiles.push(f.trim()));
    text = text.replace(PAPERCLIP_REGEX, '').trim();
  }

  return { text, attachedFiles };
}

// ── Thought collapsible block (committed messages) ──────────────────────────

/**
 * Build the body that goes between `[TOOL_START]` and `[/TOOL]` markers
 * for HISTORY rendering. Output shape mirrors what the live SSE handler
 * emits inline (the `tool_start` case below) so `parseThoughtEntries`
 * renders both paths identically:
 *   - bare tool name when no input
 *   - "name\n  k=v, k=v" for key/value tool_input
 *   - "name\n```lang\ncode\n```" for code_interpreter calls
 *
 * If you change either side, update the other to keep history and live
 * displays consistent.
 */
function buildToolStartBody(toolName: string, toolInput?: Record<string, unknown> | null): string {
  if (!toolInput || Object.keys(toolInput).length === 0) {
    return toolName;
  }
  const ciiAction = (toolInput.code_interpreter_input as Record<string, unknown> | undefined)
    ?.action as Record<string, unknown> | undefined;
  const code = ciiAction?.code as string | undefined;
  if (code) {
    const lang = String(ciiAction?.language ?? 'python');
    return `${toolName}\n\`\`\`${lang}\n${code}\n\`\`\``;
  }
  const pairs = Object.entries(toolInput)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(', ');
  return pairs ? `${toolName}\n  ${pairs}` : toolName;
}

/** Parse structured thought text into renderable tool entries */
interface ToolEntry {
  type: 'tool_start' | 'tool_done' | 'file_output' | 'interrupt';
  name: string;
  detail: string;       // input display or status
  code?: string;        // extracted code block
  codeLang?: string;    // language for code block
  status?: string;      // success / error
}

function parseThoughtEntries(thought: string): ToolEntry[] {
  const entries: ToolEntry[] = [];
  const regex = /\[(TOOL_START|TOOL_DONE|FILE_OUTPUT|INTERRUPT)\]([\s\S]*?)\[\/TOOL\]/g;
  let match;
  while ((match = regex.exec(thought)) !== null) {
    const tag = match[1];
    const body = match[2];

    if (tag === 'TOOL_START') {
      // First line is tool name + optional key=value, rest may be code block
      const lines = body.split('\n');
      const firstLine = lines[0];
      const rest = lines.slice(1).join('\n').trim();

      // Extract code block if present: ```lang\n...\n```
      let code: string | undefined;
      let codeLang: string | undefined;
      let detail = '';
      const codeMatch = rest.match(/^```(\w*)\n([\s\S]*?)\n```$/);
      if (codeMatch) {
        codeLang = codeMatch[1] || 'python';
        code = codeMatch[2];
      } else if (rest) {
        detail = rest;
      }

      // firstLine may have inline key=value params after the tool name
      const trimmed = firstLine.trim();
      const spaceIdx = trimmed.indexOf(' ');
      const name = spaceIdx >= 0 ? trimmed.slice(0, spaceIdx) : trimmed;
      if (!code && !detail && spaceIdx >= 0) {
        const inlineDetail = trimmed.slice(spaceIdx + 1).trim();
        if (inlineDetail) detail = inlineDetail;
      }

      entries.push({ type: 'tool_start', name, detail, code, codeLang });
    } else if (tag === 'TOOL_DONE') {
      const parts = body.split('|');
      const name = parts[0]?.trim() || '';
      const status = parts[1]?.trim() || 'success';
      entries.push({ type: 'tool_done', name, detail: '', status });
    } else if (tag === 'FILE_OUTPUT') {
      entries.push({ type: 'file_output', name: body.trim(), detail: '' });
    } else if (tag === 'INTERRUPT') {
      const parts = body.split('|');
      entries.push({ type: 'interrupt', name: parts[0]?.trim() || '?', detail: parts.slice(1).join('|').trim() });
    }
  }
  return entries;
}

/** Merge tool_start + tool_done into display items */
interface ThoughtItem {
  name: string;
  status: 'running' | 'success' | 'error';
  detail: string;
  code?: string;
  codeLang?: string;
  type: 'tool' | 'file' | 'interrupt';
}

function mergeThoughtItems(entries: ToolEntry[]): ThoughtItem[] {
  const items: ThoughtItem[] = [];
  // Track pending tool_starts by order (tool_done may have empty name)
  const pendingStarts: number[] = []; // indices into items

  for (const entry of entries) {
    if (entry.type === 'tool_start') {
      const idx = items.length;
      items.push({
        name: entry.name,
        status: 'running',
        detail: entry.detail,
        code: entry.code,
        codeLang: entry.codeLang,
        type: 'tool',
      });
      pendingStarts.push(idx);
    } else if (entry.type === 'tool_done') {
      const status = entry.status === 'error' ? 'error' : 'success';
      if (entry.name) {
        // Match by name
        const idx = [...pendingStarts].reverse().find(i => items[i].name === entry.name);
        if (idx !== undefined) {
          items[idx].status = status as 'success' | 'error';
          pendingStarts.splice(pendingStarts.indexOf(idx), 1);
        } else {
          // No matching start — add standalone
          items.push({ name: entry.name, status: status as 'success' | 'error', detail: '', type: 'tool' });
        }
      } else {
        // Empty name — match first pending start
        const idx = pendingStarts.shift();
        if (idx !== undefined) {
          items[idx].status = status as 'success' | 'error';
        }
      }
    } else if (entry.type === 'file_output') {
      items.push({ name: entry.name, status: 'success', detail: '', type: 'file' });
    } else if (entry.type === 'interrupt') {
      items.push({ name: entry.name, status: 'error', detail: entry.detail, type: 'interrupt' });
    }
  }
  return items;
}

function ThoughtBlock({ thought, isStreaming, onFileClick }: { thought: string; isStreaming?: boolean; onFileClick?: (path: string) => void }) {
  const { t } = useTranslation('chat');
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);

  const entries = parseThoughtEntries(thought);
  const items = mergeThoughtItems(entries);

  // Streaming → always open; completed → collapsed (unless user manually toggled)
  const open = isStreaming ? true : (manualToggle ?? false);

  const handleToggle = () => {
    if (!isStreaming) setManualToggle(prev => !(prev ?? false));
  };

  // If no structured items found, fall back to markdown rendering
  if (items.length === 0 && thought.trim()) {
    return (
      <div className={`chat-msg__thought ${open ? 'chat-msg__thought--open' : ''}`}>
        <button className="chat-msg__thought-toggle" onClick={handleToggle} aria-expanded={open}>
          <span className="chat-msg__thought-status-icon">
            <StatusIndicator type="in-progress" />
          </span>
          <span className="chat-msg__thought-label">{t('thought.thinking')}</span>
          <span className="chat-msg__thought-chevron"><Icon name={open ? 'angle-up' : 'angle-down'} /></span>
        </button>
        {open && (
          <div className="chat-msg__thought-body">
            {isStreaming && (
              <div className="chat-msg__thought-hint">
                <SafeTrans
                  t={t}
                  i18nKey="thought.hint"
                  components={{ icon: <img src="/icons/icon-chat-add.svg" alt={t('thought.newChatAlt')} className="chat-msg__thought-hint-icon" /> }}
                />
              </div>
            )}
            <MarkdownRenderer markdown={thought} />
          </div>
        )}
      </div>
    );
  }

  const label = isStreaming ? t('thought.thinking') : t('thought.completed');

  return (
    <div className={`chat-msg__thought ${open ? 'chat-msg__thought--open' : ''}`}>
      <button className="chat-msg__thought-toggle" onClick={handleToggle} aria-expanded={open}>
        <span className="chat-msg__thought-status-icon">
          {isStreaming ? (
            <StatusIndicator type="in-progress" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="9" fill="#0f141a"/><path d="M5.5 9.5L7.5 11.5L12.5 6.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
        </span>
        <span className="chat-msg__thought-label">{label}</span>
        <span className="chat-msg__thought-chevron"><Icon name={open ? 'angle-up' : 'angle-down'} /></span>
      </button>
      {open && (
        <div className="chat-msg__thought-body">
          {isStreaming && (
            <div className="chat-msg__thought-hint">
              <SafeTrans
                t={t}
                i18nKey="thought.hint"
                components={{ icon: <img src="/icons/icon-chat-add.svg" alt={t('thought.newChatAlt')} className="chat-msg__thought-hint-icon" /> }}
              />
            </div>
          )}
          {items.map((item, i) => (
            <div key={i} className="thought-item">
              <div className="thought-item__header">
                <span className={`thought-item__icon thought-item__icon--${item.status}`}>
                  {item.type === 'file' ? <Icon name="file" size="small" /> : item.type === 'interrupt' ? '⚠️' : item.status === 'success' ? '✓' : item.status === 'error' ? '✗' : <StatusIndicator type="loading" />}
                </span>
                {item.type === 'file' && onFileClick ? (
                  <span className="thought-item__file-link">
                    <Link variant="secondary" onFollow={(e) => { e.preventDefault(); onFileClick(item.name); }}>
                      <span className="thought-item__name">{item.name}</span>
                    </Link>
                  </span>
                ) : (
                  <span className="thought-item__name">{item.name}</span>
                )}
              </div>
              {item.detail && (
                <div className="thought-item__detail">{item.detail}</div>
              )}
              {item.code && (
                <div className="thought-item__code">
                  <pre className="thought-item__code-block">
                    <code>{item.code}</code>
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Suggested Questions (floating card) ─────────────────────────────────────

function SuggestedQuestions({ questions, onSelect }: { questions: string[]; onSelect: (q: string) => void }) {
  const { t } = useTranslation('chat');
  return (
    <div className="chat-panel__suggestions-wrap">
      <div className="chat-panel__suggestions">
        <div className="chat-panel__suggestions-title">
          <span className="chat-panel__suggestions-icon">💡</span>
          <span>{t('suggestions.title')}</span>
        </div>
        <div className="chat-panel__suggestions-list">
          {questions.map((q, idx) => (
            <button
              key={idx}
              className="chat-panel__suggestion-chip"
              onClick={() => onSelect(q)}
              title={q}
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── File Output Cards (agent-generated files) ──────────────────────────────

function FileOutputCards({ files, onFileClick }: { files: FileOutputInfo[]; onFileClick: (path: string) => void }) {
  if (!files.length) return null;
  return (
    <div className="file-output-cards">
      {files.map((f, i) => (
        <button
          key={i}
          className="file-output-card"
          onClick={() => onFileClick(f.path)}
          title={f.path}
        >
          <Icon name="file" size="normal" />
          <span className="file-output-card__name">{f.filename}</span>
          <Icon name="angle-right" size="small" />
        </button>
      ))}
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * ChatPanel — public entry point.
 *
 * Routes AG-UI agents to AgUiChatPanel and legacy agents to ChatPanelLegacy.
 * The split is intentional: React requires hooks to be called unconditionally
 * on every render. Putting an early return before hook calls inside one
 * component violates that rule when `isCollapsed` changes at runtime.
 * Keeping each path as its own component gives each a stable hook order.
 */
export default function ChatPanel({ isCollapsed, onToggle, hideFullscreen, agentName = 'rocky' }: ChatPanelProps) {
  // Asset / Use Case / storyline scopes live in ChatPanelContext, outside either
  // panel, so remounting a panel does not clear them. ChatPanelLegacy clears them
  // on its own agent switch, but that cannot see a switch that unmounts it (legacy
  // -> AG-UI) and AgUiChatPanel has no reset at all. Clearing here covers every
  // switch: legacy <-> AG-UI and AG-UI <-> AG-UI.
  const { setAssetScope, setUcScope, setStorylineScope } = useChatPanel();
  const prevAgentRef = useRef(agentName);
  useEffect(() => {
    if (prevAgentRef.current === agentName) return;
    prevAgentRef.current = agentName;
    setAssetScope(null);
    setUcScope(null);
    setStorylineScope(null);
  }, [agentName, setAssetScope, setUcScope, setStorylineScope]);

  if (AGUI_AGENTS.has(agentName)) {
    // key: switching between two AG-UI agents must remount the panel. It keeps
    // its thread, input, scopes and pending gates in component state, with no
    // reset on an agentName change, so a reused instance would carry one
    // agent's session over to the other (and persist it under the new agent).
    return (
      <AgUiChatPanel
        key={agentName}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
        hideFullscreen={hideFullscreen}
        agentName={agentName}
      />
    );
  }
  return (
    <ChatPanelLegacy
      isCollapsed={isCollapsed}
      onToggle={onToggle}
      hideFullscreen={hideFullscreen}
      agentName={agentName}
    />
  );
}

function ChatPanelLegacy({ isCollapsed, onToggle, hideFullscreen, agentName = 'rocky' }: ChatPanelProps) {
  const { t } = useTranslation('chat');
  // Rocky is the flagship agent and owns the "Rocky's Pack" skill row. The
  // Marketing Agent shares Rocky's avatar; other agents get the generic one.
  const isRocky = agentName === 'rocky';
  const assistantAvatarUrl = isRocky || agentName === 'marketing_agent' ? '/icons/avatar-rocky.png' : undefined;
  const { uid, display_name } = useAuth();
  const { isImpersonating, effectiveUid } = useImpersonation();
  // The user's agent from AgentContext (manual choice, else the job-role
  // default). Distinct from the agentName prop, which on the main app can also
  // come from dev/beta's ?agent=... override. The reset-on-switch effect keys
  // off this — not the prop — so the override does not wipe the session.
  const { currentAgent } = useAgent();
  const { senderInfo } = useUserProfile();
  const { metadata } = useReportMetadata();
  const location = useLocation();
  const openWorkspaceFile = useOpenWorkspaceFile();
  const { selectedFile, refreshWorkspace } = useWorkspaceNav();
  // The workspace "context file" (the currently-open report on WorkspacePage) is
  // only meaningful when the user is actually on the workspace surface. Off `/`
  // (e.g. /ivt/*, /skills) we treat it as unset — the toolbar chip disappears
  // and the file isn't quietly attached to messages the user sends from another
  // surface. selectedFile itself is preserved so returning to `/` restores the
  // prior open file.
  const contextFile = location.pathname === '/' ? selectedFile : null;
  const { pendingPrompt, clearPendingPrompt, draftPrompt, clearDraftPrompt, setChatBusy, setChatQueueFull, chatFullscreen, toggleFullscreen, assetScope, setAssetScope, ucScope, setUcScope, storylineScope, setStorylineScope, waveQueryScope } = useChatPanel();
  const [sessionId, setSessionId] = useState(() => `rocky-${crypto.randomUUID()}`);
  const [messages, setMessages] = useState<DisplayMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: getAgentWelcome(agentName),
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [contextPercent, setContextPercent] = useState<number>(0);

  useEffect(() => { setChatBusy(isLoading); }, [isLoading, setChatBusy]);

  // Track whether user has chatted since the last suggestion-reset event
  // (session start or report selection). Controls suggested-question visibility.
  const [hasChatted, setHasChatted] = useState(false);
  // Whether to show default (non-report) suggested questions.
  // True only at session start; set to false once a report is selected.
  const [showDefaultSuggestions, setShowDefaultSuggestions] = useState(true);

  // ── File attachment state ───────────────────────────────────────────
  // Unified pending file model — avoids parallel array desync (AutoSDE #1)
  interface PendingFile {
    id: string;         // stable key
    file: File;         // original File object
    status: 'uploading' | 'done' | 'error';
    uploaded?: UploadedFile;
  }
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  // Derive uploading state from pendingFiles — reactive, no manual bookkeeping
  const isUploading = pendingFiles.some(pf => pf.status === 'uploading');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // True while the user is dragging file(s) anywhere over the window — used to
  // reveal the drop overlay only when there's actually a file to drop.
  const { areFilesDragging } = useFilesDragging();
  // Whether the selected workspace file is included as context
  const [contextFileEnabled, setContextFileEnabled] = useState(true);
  // Track whether the context file label has been shown in chat (only show on first send)
  const contextFileLabelShownRef = useRef(false);
  // Track which asset-scope id has already been labeled on a user message. On
  // (re)bind — including clearing to null then re-binding — the id differs from
  // the last labeled one and the next user message re-shows the "KB: <name>" tag.
  const assetScopeLabelShownForRef = useRef<string | null>(null);
  // UC parallel of assetScopeLabelShownForRef (keyed by solutionMapId as a string).
  const ucScopeLabelShownForRef = useRef<string | null>(null);
  // Storyline parallel of assetScopeLabelShownForRef (keyed by storyline id).
  const storylineScopeLabelShownForRef = useRef<string | null>(null);
  // The "label shown" refs are only advanced on SEND, never on (re)bind/clear — so
  // without this, switching back to a previously-bound scope (or clearing then
  // rebinding the same one) would leave a stale ref that suppresses the "KB: <name>"
  // message tag. This effect fires ONLY when a scope's identity changes (bind /
  // rebind / clear — sends don't change scope), so resetting both refs here is
  // exactly "show the tag once per (re)bind" for asset, UC, and switch-backs alike.
  useEffect(() => {
    assetScopeLabelShownForRef.current = null;
    ucScopeLabelShownForRef.current = null;
    storylineScopeLabelShownForRef.current = null;
  }, [assetScope, ucScope, storylineScope]);

  // Streaming states — separate from messages array for RAF-speed updates
  const [streamingText, setStreamingText] = useState('');
  const [streamingThought, setStreamingThought] = useState('');

  // ── Resize state ────────────────────────────────────────────────────
  const [chatWidth, setChatWidth] = useState(407);
  const [isResizing, setIsResizing] = useState(false);
  const chatWidthRef = useRef(407);
  chatWidthRef.current = chatWidth;

  // ── Message pagination (older messages on scroll-to-top) ─────────
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const messagesNextTokenRef = useRef<string | undefined>(undefined);
  const historySessionIdRef = useRef<string | undefined>(undefined);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const abortRef = useRef<AbortController | null>(null);
  const streamBufRef = useRef('');           // agentFinalResponse accumulator
  const thoughtBufRef = useRef('');          // agentThought accumulator
  const fileOutputsBufRef = useRef<FileOutputInfo[]>([]); // file_output accumulator
  // Latest `follow_ups` event of the current send. A resume / retry replays the
  // stream, so a later event simply replaces an earlier one (no duplicates).
  const followUpsBufRef = useRef<FollowUps | null>(null);
  const elapsedSRef = useRef<number | undefined>(undefined); // timing from gateway
  const lastFlushedRef = useRef('');         // last flushed streamingText
  const lastFlushedThoughtRef = useRef('');  // last flushed streamingThought
  const rafIdRef = useRef<number>(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Model picker: options + the user's global choice, sent as `model`.
  const modelChoice = useModelChoice();
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const sendGenerationRef = useRef(0);
  const runtimeConfirmedRef = useRef(false);
  const conversationIdRef = useRef<string>('');

  // ── Pending question queue (sent while agent is streaming) ──────────
  const [pendingQueue, setPendingQueue] = useState<PendingQuestion[]>([]);
  const drainRef = useRef(false);
  const [queueToast, setQueueToast] = useState<string | null>(null);
  const queueToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showQueueToast = useCallback((msg: string) => {
    if (queueToastTimerRef.current) clearTimeout(queueToastTimerRef.current);
    setQueueToast(msg);
    queueToastTimerRef.current = setTimeout(() => setQueueToast(null), 2500);
  }, []);
  useEffect(() => () => {
    if (queueToastTimerRef.current) clearTimeout(queueToastTimerRef.current);
  }, []);
  // Mirror queue-full state to context so external triggers (e.g.
  // WorkspaceHome cards) can early-return before starting any animation.
  useEffect(() => {
    setChatQueueFull(pendingQueue.length >= PENDING_QUEUE_MAX);
  }, [pendingQueue.length, setChatQueueFull]);

  // ── Reset state on agent switch ─────────────────────────────────────
  const prevAgentRef = useRef(currentAgent);
  useEffect(() => {
    if (prevAgentRef.current === currentAgent) return;
    prevAgentRef.current = currentAgent;
    // Tear down any in-flight stream before resetting, so a response that is
    // still streaming for the previous agent cannot commit into the new agent's
    // fresh session. Mirrors handleNewSession's teardown.
    sendGenerationRef.current++;
    abortRef.current?.abort();
    stopStreamFlush();
    abortRef.current = null;
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';
    // Reset chat to fresh state for the new agent
    setIsLoading(false);
    setStreamingText('');
    setStreamingThought('');
    setSessionId(`rocky-${crypto.randomUUID()}`);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: getAgentWelcome(agentName),
      timestamp: new Date(),
    }]);
    setInputText('');
    setHasChatted(false);
    setShowDefaultSuggestions(true);
    setPendingQueue([]);
    setShowHistory(false);
    messagesNextTokenRef.current = undefined;
    historySessionIdRef.current = undefined;
    // Bump the older-messages generation so any in-flight loadOlderMessages
    // fetch is discarded and can't prepend the previous agent's history.
    olderMsgGenRef.current++;
    // Clear scopes
    setAssetScope(null);
    setUcScope(null);
    setStorylineScope(null);
  }, [currentAgent, agentName, setAssetScope, setUcScope, setStorylineScope]);

  // ── Equipment / Skill Pack state ────────────────────────────────────
  const [equipmentItems, setEquipmentItems] = useState<EquipmentItem[]>([]);

  // ── Session count (for suggested questions logic) ──────────────────
  const [sessionCount, setSessionCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    gatewayClient.listSessions({ source: 'rocky', limit: 50, agent_id: agentName }).then(resp => {
      if (!cancelled) {
        const total = resp.next_token ? 100 : resp.sessions.length;
        setSessionCount(total);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [agentName]);

  // Picked once per session in every locale, then shown in the current one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultSuggestedQuestions = useMemo(() => getDefaultSuggestedQuestions(sessionCount, agentName), [sessionId, sessionCount, agentName]);
  const { locale } = useLocale();

  const groupedSkills = useMemo(() => {
    const map = new Map<string, CatalogItem[]>();
    for (const item of equipmentItems) {
      if (!item.skill?.category) continue;
      const rawCat = item.skill.category.toLowerCase();
      // Normalize to canonical keys
      let cat: string;
      if (rawCat === 'dataretriever') cat = 'DataRetriever';
      else if (rawCat === 'hedwig') cat = 'Hedwig';
      else if (rawCat === 'cheetah') cat = 'Cheetah';
      else if (rawCat === 'ivt') cat = 'IVT';
      else cat = item.skill.category; // keep original for unknown categories
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item.skill);
    }
    return map;
  }, [equipmentItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      cancelAnimationFrame(rafIdRef.current);
      resizeCleanupRef.current?.();
    };
  }, []);

  // Reset hasChatted when a different report is selected so suggested questions reappear
  // Reset hasChatted when a different report is selected so suggested questions reappear.
  // Also disable default suggestions once a report is selected.
  useEffect(() => {
    setHasChatted(false);
    setContextFileEnabled(true);
    contextFileLabelShownRef.current = false;
    if (selectedFile) {
      setShowDefaultSuggestions(false);
    }
  }, [selectedFile?.key]);

  // Scroll to bottom when suggested questions appear (file selected + metadata loaded)
  useEffect(() => {
    if (!hasChatted && selectedFile && metadata?.suggested_questions?.length) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (container) {
            isProgrammaticScrollRef.current = true;
            userScrolledUpRef.current = false;
            setUserScrolledUp(false);
            container.scrollTop = container.scrollHeight;
            lastScrollTopRef.current = container.scrollTop;
            requestAnimationFrame(() => {
              isProgrammaticScrollRef.current = false;
            });
          }
        });
      });
    }
  }, [hasChatted, selectedFile?.key, metadata?.suggested_questions]);

  // ── Load equipped skills ──────────────────────────────────────────
  useEffect(() => {
    // Skill packs are a Rocky-only feature; other agents (e.g. marketing_agent)
    // don't surface the "Rocky's Pack" row, so skip the equipment fetch.
    if (!uid || !isRocky) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getSkillClient().send(
          new ListEquipmentCommand({ userAlias: uid, agentName, maxResults: 100 }),
        );
        if (import.meta.env.DEV) console.log('[ChatPanel] ListEquipment response:', resp);
        if (!cancelled && resp.items) {
          setEquipmentItems(resp.items);
        }
      } catch (err) {
        console.warn('[ChatPanel] Failed to load equipment:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, isRocky, agentName]);

  // ── Skill card handlers ───────────────────────────────────────────
  const handleSkillSelect = useCallback((displayName: string) => {
    setInputText(`Execute ${displayName} skill`);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  // ── Drag-to-resize ────────────────────────────────────────────────
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Clean up any prior session whose mouseup was missed (e.g. focus loss)
    resizeCleanupRef.current?.();
    const startX = e.clientX;
    const startWidth = chatWidthRef.current;

    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX;
      const newWidth = Math.min(Math.max(startWidth + delta, 280), 700);
      setChatWidth(newWidth);
    };

    const cleanup = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      resizeCleanupRef.current = null;
    };

    const onMouseUp = () => cleanup();

    resizeCleanupRef.current = cleanup;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // ── Smart auto-scroll: only scroll if user is near the bottom ─────
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const userScrolledUpRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);

  useEffect(() => {
    if (!userScrolledUpRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        isProgrammaticScrollRef.current = true;
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
          lastScrollTopRef.current = container.scrollTop;
          requestAnimationFrame(() => {
            isProgrammaticScrollRef.current = false;
          });
        });
      }
    }
  }, [messages, streamingText, streamingThought, pendingQueue.length]);

  // When messages change (streaming end commit) or loading stops, check if still at bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !userScrolledUpRef.current) return;
    requestAnimationFrame(() => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 50) {
        userScrolledUpRef.current = false;
        setUserScrolledUp(false);
      }
    });
  }, [messages, isLoading]);

  // ── RAF flush loop ────────────────────────────────────────────────

  const startStreamFlush = useCallback(() => {
    const tick = () => {
      const buf = streamBufRef.current;
      if (buf !== lastFlushedRef.current) {
        lastFlushedRef.current = buf;
        setStreamingText(buf);
      }
      const tBuf = thoughtBufRef.current;
      if (tBuf !== lastFlushedThoughtRef.current) {
        lastFlushedThoughtRef.current = tBuf;
        setStreamingThought(tBuf);
      }
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
  }, []);

  const stopStreamFlush = useCallback(() => {
    cancelAnimationFrame(rafIdRef.current);
  }, []);

  // ── Auto-resize textarea ──────────────────────────────────────────

  const [inputHeight, setInputHeight] = useState<number | null>(null);
  const inputResizingRef = useRef(false);
  const inputResizeStartYRef = useRef(0);
  const inputResizeStartHeightRef = useRef(0);

  const handleInputResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    inputResizingRef.current = true;
    inputResizeStartYRef.current = e.clientY;
    const wrapper = (e.target as HTMLElement).parentElement;
    inputResizeStartHeightRef.current = wrapper?.offsetHeight ?? 80;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!inputResizingRef.current) return;
      const delta = inputResizeStartYRef.current - ev.clientY;
      const newHeight = Math.max(80, Math.min(inputResizeStartHeightRef.current + delta, 300));
      setInputHeight(newHeight);
    };

    const handleMouseUp = () => {
      inputResizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const syncTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el || inputHeight !== null) return;
    if (!inputText) {
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  }, [inputHeight, inputText]);

  useEffect(() => {
    syncTextareaHeight();
  }, [inputText, syncTextareaHeight]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  }, []);

  // ── Send message ──────────────────────────────────────────────────

  // ── Shared SSE stream consumer ────────────────────────────────────
  // Reads an SSE response body and dispatches events to streamBufRef / thoughtBufRef.
  // Returns true if an error was encountered.
  const consumeSSEStream = useCallback(async (response: Response): Promise<boolean> => {
    let hadError = false;

    if (response.status === 401 || response.status === 403) {
      hadError = true;
      streamBufRef.current = `⚠️ ${t('errors.auth')}`;
      return hadError;
    }

    if (!response.ok) {
      hadError = true;
      let errMsg = `HTTP ${response.status}`;
      try { const errBody = await response.json(); errMsg = errBody?.error?.message ?? errMsg; } catch { /* */ }
      streamBufRef.current = `⚠️ ${errMsg}`;
      return hadError;
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {
        if (!chunk.trim()) continue;

        let eventType = '';
        let dataStr = '';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) dataStr += (dataStr ? '\n' : '') + line.slice(6);
        }

        if (!dataStr) continue;

        let data: Record<string, unknown> = {};
        try { data = JSON.parse(dataStr); } catch { continue; }

        const type = eventType || (typeof data.type === 'string' ? data.type : '');

        switch (type) {
          case 'text_delta':
            if (typeof data.delta === 'string') {
              streamBufRef.current += data.delta;
              runtimeConfirmedRef.current = true;
            }
            break;

          case 'tool_start': {
            runtimeConfirmedRef.current = true;
            const toolName = String(data.name ?? '?');
            const toolInput = data.input as Record<string, unknown> | undefined;
            let inputDisplay = '';
            if (toolInput && Object.keys(toolInput).length > 0) {
              const ciiAction = (toolInput.code_interpreter_input as Record<string, unknown>)?.action as Record<string, unknown> | undefined;
              const code = ciiAction?.code as string | undefined;
              if (code) {
                const lang = String(ciiAction?.language ?? 'python');
                inputDisplay = `\n\`\`\`${lang}\n${code}\n\`\`\``;
              } else {
                const pairs = Object.entries(toolInput)
                  .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
                  .join(', ');
                inputDisplay = pairs ? `\n  ${pairs}` : '';
              }
            }
            thoughtBufRef.current += `[TOOL_START]${toolName}${inputDisplay}[/TOOL]\n`;
            break;
          }

          case 'tool_done': {
            const doneToolName = String(data.name || '');
            const status = String(data.status ?? '?');
            thoughtBufRef.current += `[TOOL_DONE]${doneToolName}|${status}[/TOOL]\n`;
            break;
          }

          case 'file_output':
            thoughtBufRef.current += `[FILE_OUTPUT]${String(data.path ?? '?')}[/TOOL]\n`;
            // Also capture structured metadata for file output cards
            fileOutputsBufRef.current = [...fileOutputsBufRef.current, {
              path: String(data.path ?? ''),
              filename: String(data.filename ?? data.path ?? '').split('/').pop() || 'file',
              s3Key: typeof data.s3_key === 'string' ? data.s3_key : undefined,
              contentType: typeof data.content_type === 'string' ? data.content_type : undefined,
              size: typeof data.size === 'number' ? data.size : undefined,
            }];
            break;

          case 'interrupt':
            thoughtBufRef.current += `[INTERRUPT]${String(data.name ?? '?')}|tool=${String(data.tool ?? '?')}[/TOOL]\n`;
            break;

          case 'error':
            hadError = true;
            streamBufRef.current += `\n\n⚠️ ${String(data.message ?? t('messages.unknownError'))}`;
            break;

          case 'message_start':
            if (typeof data.conversationId === 'string') {
              conversationIdRef.current = data.conversationId;
            }
            break;

          case 'message_done':
            if (typeof data.text === 'string' && !streamBufRef.current) {
              streamBufRef.current = data.text;
            }
            break;

          case 'metadata': {
            const usage = data.usage as Record<string, number> | undefined;
            if (usage?.inputTokens) {
              setContextPercent(Math.min(100, (usage.inputTokens / 1_000_000) * 100));
            }
            break;
          }

          case 'timing':
            if (typeof data.elapsed_s === 'number') {
              elapsedSRef.current = data.elapsed_s;
            }
            break;

          case 'follow_ups': {
            // Attached to the answer when the stream ends (see commit below).
            const followUps = parseFollowUpsEvent(data);
            if (followUps) followUpsBufRef.current = followUps;
            break;
          }

          case 'state_snapshot': {
            // Side-output channel: pushed to a non-chat panel store, NOT
            // into the chat bubble. Subscribers (e.g. <MotionWaveSelectPanel>)
            // re-render via useAgentState(namespace). See
            // stores/agentStateStore.ts and Gateway rocky formatter.
            const ns = typeof data.namespace === 'string' ? data.namespace : 'default';
            const snapshot = data.snapshot;
            setAgentState(ns, snapshot);
            break;
          }

          default:
            if (type) {
              console.debug('[ChatPanel] Unhandled SSE event type:', type, data);
            }
            break;
        }
      }
    }

    // Flush remaining bytes
    buffer += decoder.decode();
    if (buffer.trim()) {
      let dataStr = '';
      for (const line of buffer.split('\n')) {
        if (line.startsWith('data: ')) dataStr += (dataStr ? '\n' : '') + line.slice(6);
      }
      if (dataStr) {
        try {
          const data = JSON.parse(dataStr);
          if (typeof data.delta === 'string') streamBufRef.current += data.delta;
          if (typeof data.text === 'string' && !streamBufRef.current) streamBufRef.current = data.text;
        } catch { /* ignore */ }
      }
    }

    return hadError;
  }, [t]);


  // ── File attachment handlers ──────────────────────────────────────────

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Core upload path — shared by the file picker and the drag-and-drop
  // dropzone. Takes a plain File[] so both entry points reuse the same
  // type/size validation and S3 upload logic.
  const processFiles = useCallback(async (selected: File[]) => {
    const files = [...selected];
    if (!files.length) return;

    // Whitelist enforcement — the agent can only read the extensions in
    // getAllowedUploadExts(). This runs for BOTH the picker and drag-and-drop
    // (the <input accept> attribute does not filter dropped files), so e.g.
    // .zip/.exe/.mp4 dropped onto the panel are rejected here rather than
    // uploaded and then silently unreadable. Office/PowerPoint are included
    // only when OFFICE_UPLOAD_ENABLED.
    const allowed = getAllowedUploadExts();
    const isAllowed = (f: File) => allowed.some(ext => f.name.toLowerCase().endsWith(ext));
    const rejected = files.filter(f => !isAllowed(f));
    if (rejected.length > 0) {
      const exts = [...new Set(rejected.map(f => f.name.split('.').pop()?.toUpperCase() || '?'))].join(t('files.listSeparator'));
      alert(t('files.unsupported', { exts }));
      const supported = files.filter(isAllowed);
      if (!supported.length) return;
      // Continue with supported files only
      files.length = 0;
      files.push(...supported);
    }

    // Create PendingFile entries with stable IDs (AutoSDE #1)
    const newEntries: PendingFile[] = files.map(f => ({
      id: crypto.randomUUID(),
      file: f,
      status: 'uploading' as const,
    }));
    setPendingFiles(prev => [...prev, ...newEntries]);

    try {
      const result = await gatewayClient.uploadFiles(files, sessionId);
      // Match uploaded results back to pending entries by index
      setPendingFiles(prev => prev.map(pf => {
        const idx = newEntries.findIndex(ne => ne.id === pf.id);
        if (idx === -1) return pf;  // not from this batch
        const uploaded = result.files[idx];
        return uploaded
          ? { ...pf, status: 'done' as const, uploaded }
          : { ...pf, status: 'error' as const };
      }));
      if (result.errors.length > 0) {
        console.warn('Upload errors:', result.errors);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setPendingFiles(prev => prev.map(pf =>
        newEntries.some(ne => ne.id === pf.id) ? { ...pf, status: 'error' as const } : pf
      ));
    } finally {
    }
  }, [sessionId, t]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Reset input so re-selecting the same file triggers onChange (AutoSDE #5)
    if (fileInputRef.current) fileInputRef.current.value = '';
    void processFiles(files);
  }, [processFiles]);

  // Dropzone onChange gives the current File[] value directly.
  const handleFileDrop = useCallback((files: File[]) => {
    void processFiles(files);
  }, [processFiles]);

  const handleRemoveFile = useCallback((id: string) => {
    setPendingFiles(prev => prev.filter(pf => pf.id !== id));
  }, []);

  const handleSend = useCallback(async (text?: string, opts?: { fromQueue?: boolean; followUpId?: string }) => {
    // While streaming with an empty queue, an empty submit (Enter or Send button)
    // becomes the email-fallback prompt — user is opting out of waiting.
    const rawText = text ?? inputText;
    const trimmed = rawText.trim();
    const sendText = (!trimmed && isLoading && pendingQueue.length === 0 && !opts?.fromQueue)
      ? t('input.emailFallback')
      : trimmed;
    // Guard: block empty send / mid-upload (AutoSDE #6)
    if (!sendText || isUploading) return;

    // While streaming: enqueue instead of sending. The drain effect
    // re-invokes handleSend with { fromQueue: true } once isLoading flips
    // false (natural end or stop), so this branch only fires for fresh
    // user input.
    if (isLoading) {
      if (pendingQueue.length >= PENDING_QUEUE_MAX) {
        showQueueToast(t('queue.full', { max: PENDING_QUEUE_MAX }));
        return;
      }
      setPendingQueue(prev => [
        ...prev,
        { id: crypto.randomUUID(), text: sendText, createdAt: Date.now() },
      ]);
      if (!text) setInputText('');
      if (textareaRef.current) textareaRef.current.style.height = '';
      return;
    }

    // Queue drain sends text-only — never auto-attach files the user
    // happened to add after enqueue. Snapshotting attachments at enqueue
    // time would be the alternative; keeping queued sends file-less is
    // simpler and matches the spec's "queue holds text only" model.
    const doneFiles = opts?.fromQueue
      ? []
      : pendingFiles.filter(pf => pf.status === 'done' && pf.uploaded);

    const thisGeneration = ++sendGenerationRef.current;

    const fileLabel = doneFiles.length > 0
      ? '\n\n📎 ' + doneFiles.map(pf => pf.file.name).join(', ')
      : '';
    let contextFileName: string | undefined;
    if (contextFile && contextFileEnabled && !contextFileLabelShownRef.current) {
      contextFileName = contextFile.name;
      contextFileLabelShownRef.current = true;
    }
    let assetScopeName: string | undefined;
    if (assetScope && assetScopeLabelShownForRef.current !== assetScope.assetId) {
      assetScopeName = assetScope.assetName;
      assetScopeLabelShownForRef.current = assetScope.assetId;
    }
    // UC parallel: show the "KB: <ucName>" tag once on the first message after a
    // (re)bind. asset/UC/storyline are mutually exclusive, so at most one shows.
    let ucScopeName: string | undefined;
    if (ucScope && ucScopeLabelShownForRef.current !== String(ucScope.solutionMapId)) {
      ucScopeName = ucScope.ucName;
      ucScopeLabelShownForRef.current = String(ucScope.solutionMapId);
    }
    // Storyline parallel of the above.
    let storylineScopeName: string | undefined;
    if (storylineScope && storylineScopeLabelShownForRef.current !== storylineScope.storylineId) {
      storylineScopeName = storylineScope.storylineName;
      storylineScopeLabelShownForRef.current = storylineScope.storylineId;
    }
    const userMsg: DisplayMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: sendText + fileLabel,
      timestamp: new Date(),
      contextFileName,
      assetScopeName,
      ucScopeName,
      storylineScopeName,
    };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    // Clear attached files immediately on send (files disappear from input area).
    // Skip when draining the queue — queued sends are text-only, so the
    // user's currently-attached files (added after enqueue) must survive.
    if (!opts?.fromQueue) setPendingFiles([]);
    setIsLoading(true);
    setHasChatted(true);
    userScrolledUpRef.current = false;
    setUserScrolledUp(false);

    // Reset streaming state
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    elapsedSRef.current = undefined;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';
    runtimeConfirmedRef.current = false;
    conversationIdRef.current = '';
    setStreamingText('');
    setStreamingThought('');

    if (textareaRef.current) {
      textareaRef.current.style.height = '';
    }

    startStreamFlush();

    const controller = new AbortController();
    abortRef.current = controller;

    // Build media array: uploaded files + selected workspace file (if enabled)
    const allMedia: UploadedFile[] = doneFiles.map(pf => pf.uploaded!);
    if (contextFile && contextFileEnabled) {
      const ext = contextFile.name.split('.').pop()?.toLowerCase() || '';
      const mimeMap: Record<string, string> = {
        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
        webp: 'image/webp', svg: 'image/svg+xml', pdf: 'application/pdf',
        csv: 'text/csv', json: 'application/json', md: 'text/markdown',
        html: 'text/html', txt: 'text/plain', xml: 'application/xml',
      };
      const mimetype = mimeMap[ext] || 'application/octet-stream';
      const type = mimetype.startsWith('image/') ? 'image' : 'document';
      allMedia.push({
        file_id: `ws-${contextFile.key}`,
        filename: contextFile.name,
        mimetype, type, format: ext,
        size: contextFile.size,
        s3_key: `${uid || 'anonymous'}/rocky/output/${contextFile.key}`,
        s3_uri: '', s3_bucket: '',  // gateway fills these
      });
    }

    // AG-UI ``context.items``: {description, value} entries the Runtime renders
    // into <application_context> as DATA, never instructions.
    //
    // Assembled as a list because the entries are not all one kind. The three KB
    // scopes stay mutually exclusive and keep their precedence — at most one of
    // them appears, so the agent still knows which retrieve_*_kb to call. The wave
    // query is a different sort of state and coexists with any of them.
    const contextItems: { description: string; value: string }[] = [];
    if (assetScope) {
      contextItems.push({
        description: '用户当前聚焦的 Asset',
        value: `${assetScope.assetName} (id: ${assetScope.assetId})`,
      });
    } else if (ucScope) {
      contextItems.push({
        description: '用户当前聚焦的 Use Case',
        value: `${ucScope.ucName} (solutionMapId: ${ucScope.solutionMapId})`,
      });
    } else if (storylineScope) {
      contextItems.push({
        description: '用户当前聚焦的 Storyline',
        value: `${storylineScope.storylineName} (id: ${storylineScope.storylineId})`,
      });
    }
    if (waveQueryScope) {
      // The panel owns this query and the user can edit it there — dismissing a
      // chip removes a condition. Without carrying the live spec, the agent would
      // rebuild its last one and put the removed condition back.
      contextItems.push({
        description:
          `Create Wave 面板当前生效的查询（motion ${waveQueryScope.motionId}）。` +
          '这是面板上的真实状态，用户可能已经手动删掉了某些条件；' +
          '下次生成 query_spec 时以此为准，不要恢复这里没有的条件。',
        value: waveQueryScope.specJson,
      });
    }

    // Build request body for POST /channels/rocky/events
    const requestBody = {
      sessionId,
      agent: agentName,
      messages: [{ role: 'user', content: sendText }],
      context: {
        login: uid || 'anonymous',
        source: 'rocky',
        // AG-UI ``context.items`` convention (CR-289080845). Each entry is
        // ``{description, value}``; the Runtime renders them into an
        // <application_context> block in the system prompt as data, not
        // instructions. Use for per-request focus state (e.g. the asset the
        // user is currently viewing) — NEVER for identity/authz.
        // Asset / UC / Storyline scope are mutually exclusive, so at most one items
        // entry is emitted. The agent reads the entry to decide which retrieve_*_kb
        // tool to call (retrieve_asset_kb for the Asset id / retrieve_uc_kb for the
        // UC's solutionMapId / retrieve_storyline_kb for the storyline id). Data-only
        // focus state, never identity/authz.
        ...(contextItems.length > 0 ? { items: contextItems } : {}),
        // Set when this message is a click on a follow-up suggestion; stored on
        // the user_query row so the click is measurable and restorable.
        ...(opts?.followUpId ? { follow_up_id: opts.followUpId } : {}),
        ...(metadata ? {
          report_metadata: JSON.stringify({
            data_window: metadata.data_window,
            kpi: metadata.kpi,
            highlights: metadata.highlights,
            suggested_questions: metadata.suggested_questions,
            template: metadata.template,
            sub_segments: metadata.sub_segments,
          }),
        } : {}),
      },
      ...(allMedia.length > 0 ? { media: allMedia } : {}),
      ...(senderInfo ? { sender: senderInfo } : {}),
      // Applies from this message on; absent = the agent's default model.
      ...(modelChoice.requestModel ? { model: modelChoice.requestModel } : {}),
    };

    let hadError = false;

    try {
      const url = `${appConfig.gatewayUrl}/channels/rocky/events`;
      const sseHeaders = { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' };

      let response = await fetch(url, {
        method: 'POST',
        headers: gatewayAuthHeaders(sseHeaders),
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      if (response.status === 401) {
        const newToken = await refreshAuth();
        if (newToken) {
          // Re-read the cookies: refreshAuth just rotated them.
          response = await fetch(url, {
            method: 'POST',
            headers: gatewayAuthHeaders(sseHeaders),
            body: JSON.stringify(requestBody),
            signal: controller.signal,
          });
        }
      }

      hadError = await consumeSSEStream(response);
    } catch (err: unknown) {
      if (!controller.signal.aborted) {
        // If we received message_start (agent is running), try resume instead of failing
        const convId = conversationIdRef.current;
        if (convId) {
          try {
            const resumeResp = await fetch(`${appConfig.gatewayUrl}/channels/rocky/sessions/resume`, {
              method: 'POST',
              headers: gatewayAuthHeaders({ 'Content-Type': 'application/json', 'Accept': 'text/event-stream' }),
              body: JSON.stringify({ sessionId, conversationId: convId }),
              signal: controller.signal,
            });
            hadError = await consumeSSEStream(resumeResp);
          } catch {
            hadError = true;
            const errMsg = err instanceof Error ? err.message : String(err);
            streamBufRef.current = `⚠️ ${t('errors.withMessage', { message: errMsg })}`;
          }
        } else {
          // Agent never started — retry the original request once
          try {
            const retryResp = await fetch(`${appConfig.gatewayUrl}/channels/rocky/events`, {
              method: 'POST',
              headers: gatewayAuthHeaders({ 'Content-Type': 'application/json', 'Accept': 'text/event-stream' }),
              body: JSON.stringify(requestBody),
              signal: controller.signal,
            });
            hadError = await consumeSSEStream(retryResp);
          } catch (retryErr: unknown) {
            hadError = true;
            const errMsg = retryErr instanceof Error ? retryErr.message : String(retryErr);
            streamBufRef.current = `⚠️ ${t('errors.withMessage', { message: errMsg })}`;
          }
        }
      }
    }

    stopStreamFlush();

    if (sendGenerationRef.current !== thisGeneration) return;

    // Commit final text to messages array
    if (!controller.signal.aborted) {
      const finalContent = streamBufRef.current || (hadError ? `⚠️ ${t('errors.noResponse')}` : t('messages.noResponse'));
      const finalThought = thoughtBufRef.current || undefined;
      const finalFileOutputs = fileOutputsBufRef.current.length > 0 ? [...fileOutputsBufRef.current] : undefined;
      const finalElapsedS = elapsedSRef.current;
      const finalFollowUps = hadError ? undefined : followUpsBufRef.current ?? undefined;
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: finalContent,
        thought: finalThought,
        fileOutputs: finalFileOutputs,
        timestamp: new Date(),
        elapsedS: finalElapsedS,
        followUps: finalFollowUps,
      }]);

      // If files were generated, refresh workspace nav so they appear in the file tree
      if (finalFileOutputs?.length) {
        refreshWorkspace();
      }
    }

    // Clear streaming state
    setStreamingText('');
    setStreamingThought('');
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    elapsedSRef.current = undefined;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';

    setIsLoading(false);
    abortRef.current = null;
  }, [agentName, inputText, isLoading, isUploading, pendingFiles, pendingQueue.length, showQueueToast, sessionId, uid, contextFile, contextFileEnabled, metadata, senderInfo, assetScope, ucScope, storylineScope, waveQueryScope, startStreamFlush, stopStreamFlush, refreshWorkspace, t, modelChoice.requestModel]);

  // ── Follow-up suggestion click ────────────────────────────────────
  // Mark the option (siblings go inactive, which also stops double sends), then
  // send its value on the normal path with context.follow_up_id.
  const handleFollowUpClick = useCallback((assistantMsgId: string, option: ChoiceOption) => {
    if (isLoading || isUploading) return;
    setMessages(prev => prev.map(m => (
      m.id === assistantMsgId && m.followUps
        ? { ...m, followUps: { ...m.followUps, selectedId: option.id } }
        : m
    )));
    void handleSend(option.value, { followUpId: option.id });
  }, [isLoading, isUploading, handleSend]);

  // ── Pending queue helpers ─────────────────────────────────────────
  const handleCancelQueued = useCallback((id: string) => {
    setPendingQueue(prev => prev.filter(q => q.id !== id));
  }, []);

  const handleSendQueuedNow = useCallback((id: string) => {
    setPendingQueue(prev => {
      const target = prev.find(q => q.id === id);
      if (!target) return prev;
      const others = prev.filter(q => q.id !== id);
      return [target, ...others];
    });
    // If currently streaming, stop — drain effect will pick up the head.
    // If not streaming (race: response just ended), drain effect fires
    // immediately on next state commit.
    if (isLoading) {
      // handleStop is declared below; safe at runtime via closure.
      handleStopRef.current?.();
    }
  }, [isLoading]);

  // Forward ref so handleSendQueuedNow can call handleStop without
  // a circular useCallback dependency.
  const handleStopRef = useRef<(() => void) | null>(null);

  // Drain: when stream ends and queue is non-empty, send the head.
  useEffect(() => {
    if (isLoading || isUploading) return;
    if (pendingQueue.length === 0) return;
    if (drainRef.current) return;

    drainRef.current = true;
    const [next, ...rest] = pendingQueue;
    setPendingQueue(rest);

    // Defer one microtask so React commits the setPendingQueue above
    // before handleSend flips isLoading back on. Without this, the
    // effect re-runs in the same batch and could double-fire.
    Promise.resolve().then(() => {
      drainRef.current = false;
      handleSend(next.text, { fromQueue: true });
    });
  }, [isLoading, isUploading, pendingQueue, handleSend]);

  // ── Keyboard ──────────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();  // let handleSend be single gatekeeper (supports files-only)
    }
  }, [handleSend]);

  // Auto-send prompt from context (e.g. WorkspaceHome card clicks).
  // While streaming, handleSend's enqueue branch picks it up; the cap
  // toast still fires when the queue is full.
  useEffect(() => {
    if (!pendingPrompt) return;
    clearPendingPrompt();
    handleSend(pendingPrompt);
  }, [pendingPrompt, clearPendingPrompt, handleSend]);

  // Prefill input from context without auto-sending (user decides when to send).
  // If streaming is in progress, discard the draft.
  useEffect(() => {
    if (!draftPrompt) return;
    clearDraftPrompt();
    if (!isLoading) {
      setInputText(draftPrompt);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [draftPrompt, clearDraftPrompt, isLoading]);

  // ── Stop streaming ────────────────────────────────────────────────

  const handleStop = useCallback(() => {
    sendGenerationRef.current++;

    // Notify the Gateway that the user wants to cancel this turn so the
    // Runtime can stop the agent (otherwise abort()ing the SSE only
    // closes our connection — the agent would keep running, burning
    // model tokens and possibly mutating state).
    //
    // Fire-and-forget: the local UI is updated immediately below; the
    // backend ack is irrelevant to the user, and any failure (network
    // blip, 4xx) just means the agent will finish naturally — far less
    // bad than blocking the Stop button on a slow round-trip.
    if (sessionId) {
      const headers = gatewayAuthHeaders({ 'Content-Type': 'application/json' });
      // Tag the cancel with the *current* turn's conversation_id so the
      // Runtime hook ignores this flag once a new turn (different
      // conversation_id) starts. Without this scope, clicking Stop on
      // turn N and immediately sending turn N+1 can let N's leftover
      // flag kill N+1.
      const convId = conversationIdRef.current;
      const stopBody: Record<string, string> = { session_id: sessionId };
      if (convId) stopBody.conversation_id = convId;
      fetch(`${appConfig.gatewayUrl}/channels/rocky/stop`, {
        method: 'POST',
        headers,
        body: JSON.stringify(stopBody),
      }).catch(() => {
        // Best-effort; ignore failures on purpose.
      });
    }

    abortRef.current?.abort();
    stopStreamFlush();

    const finalContent = streamBufRef.current;
    const finalThought = thoughtBufRef.current || undefined;
    const finalFileOutputs = fileOutputsBufRef.current.length > 0 ? [...fileOutputsBufRef.current] : undefined;
    const finalElapsedS = elapsedSRef.current;
    if (finalContent || finalThought) {
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: finalContent || t('messages.stopped'),
        thought: finalThought,
        fileOutputs: finalFileOutputs,
        timestamp: new Date(),
        elapsedS: finalElapsedS,
      }]);
      if (finalFileOutputs?.length) {
        refreshWorkspace();
      }
    }

    setStreamingText('');
    setStreamingThought('');
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';
    setIsLoading(false);
  }, [sessionId, stopStreamFlush, refreshWorkspace, t]);

  // Expose handleStop to handleSendQueuedNow without a circular useCallback dep.
  useEffect(() => {
    handleStopRef.current = handleStop;
  }, [handleStop]);

  // ── New session ───────────────────────────────────────────────────

  const handleNewSession = useCallback(() => {
    if (isLoading && !runtimeConfirmedRef.current) {
      const confirmed = window.confirm(t('session.switchConfirm'));
      if (!confirmed) return;
    }
    sendGenerationRef.current++;
    abortRef.current?.abort();
    stopStreamFlush();
    abortRef.current = null;
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';
    setIsLoading(false);
    setStreamingText('');
    setStreamingThought('');
    setSessionId(`rocky-${crypto.randomUUID()}`);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: getAgentWelcome(agentName),
      timestamp: new Date(),
    }]);
    setInputText('');
    setHasChatted(false);
    setShowDefaultSuggestions(true);
    setPendingQueue([]);
    messagesNextTokenRef.current = undefined;
    historySessionIdRef.current = undefined;
    olderMsgGenRef.current++;
  }, [isLoading, stopStreamFlush, t]);

  // ── Reset chat when impersonation user changes ──────────────────
  const prevEffectiveUidRef = useRef(effectiveUid);
  useEffect(() => {
    if (prevEffectiveUidRef.current !== effectiveUid) {
      prevEffectiveUidRef.current = effectiveUid;
      handleNewSession();
    }
  }, [effectiveUid, handleNewSession]);

  // ── Resume incomplete session via SSE ───────────────────────────
  const resumeSession = useCallback(async (sid: string, conversationId?: string) => {
    const thisGeneration = ++sendGenerationRef.current;

    setIsLoading(true);
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';
    setStreamingText('');
    setStreamingThought('');
    startStreamFlush();

    const controller = new AbortController();
    abortRef.current = controller;

    let hadError = false;

    try {
      const url = `${appConfig.gatewayUrl}/channels/rocky/sessions/resume`;
      const sseHeaders = { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' };

      const resumeBody = JSON.stringify({ sessionId: sid, ...(conversationId ? { conversationId } : {}) });

      let response = await fetch(url, {
        method: 'POST',
        headers: gatewayAuthHeaders(sseHeaders),
        body: resumeBody,
        signal: controller.signal,
      });

      // 401 → silent refresh + retry once
      if (response.status === 401) {
        const newToken = await refreshAuth();
        if (newToken) {
          // Re-read the cookies: refreshAuth just rotated them.
          response = await fetch(url, {
            method: 'POST',
            headers: gatewayAuthHeaders(sseHeaders),
            body: resumeBody,
            signal: controller.signal,
          });
        }
      }

      hadError = await consumeSSEStream(response);
    } catch (err: unknown) {
      if (!controller.signal.aborted) {
        hadError = true;
        const errMsg = err instanceof Error ? err.message : String(err);
        streamBufRef.current = `⚠️ ${t('errors.withMessage', { message: errMsg })}`;
      }
    }

    stopStreamFlush();

    if (sendGenerationRef.current !== thisGeneration) return;

    if (!controller.signal.aborted) {
      const finalContent = streamBufRef.current || (hadError ? `⚠️ ${t('errors.noResponse')}` : t('messages.noResponse'));
      const finalThought = thoughtBufRef.current || undefined;
      const finalFileOutputs = fileOutputsBufRef.current.length > 0 ? [...fileOutputsBufRef.current] : undefined;
      const finalFollowUps = hadError ? undefined : followUpsBufRef.current ?? undefined;
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: finalContent,
        thought: finalThought,
        fileOutputs: finalFileOutputs,
        timestamp: new Date(),
        followUps: finalFollowUps,
      }]);
      if (finalFileOutputs?.length) {
        refreshWorkspace();
      }
    }

    setStreamingText('');
    setStreamingThought('');
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';
    setIsLoading(false);
    abortRef.current = null;
  }, [consumeSSEStream, startStreamFlush, stopStreamFlush, refreshWorkspace, t]);

  // ── Load historical session ─────────────────────────────────────
  const handleLoadSession = useCallback((historicalSessionId: string, msgs: SessionMessage[], nextToken?: string) => {
    // Reset current session state
    sendGenerationRef.current++;
    abortRef.current?.abort();
    stopStreamFlush();
    abortRef.current = null;
    streamBufRef.current = '';
    thoughtBufRef.current = '';
    fileOutputsBufRef.current = [];
    followUpsBufRef.current = null;
    lastFlushedRef.current = '';
    lastFlushedThoughtRef.current = '';
    setIsLoading(false);
    setStreamingText('');
    setStreamingThought('');
    setInputText('');
    setHasChatted(true);
    setPendingQueue([]);

    // Store pagination state for older messages
    olderMsgGenRef.current++;
    messagesNextTokenRef.current = nextToken;
    historySessionIdRef.current = historicalSessionId;

    // Set the session ID to the historical one (so new messages go to same session)
    setSessionId(historicalSessionId as `${string}-${string}-${string}-${string}-${string}`);

    // Convert SessionMessage[] to DisplayMessage[], including tool_use as thought
    const displayMsgs: DisplayMessage[] = [];
    let pendingTools: SessionMessage[] = [];

    // Detect if the last agent round is incomplete (no agent_final_response after last user_query)
    const lastUserIdx = msgs.map(m => m.message_type).lastIndexOf('user_query');
    const hasAgentFinalAfterLastUser = lastUserIdx >= 0 &&
      msgs.slice(lastUserIdx + 1).some(m => m.message_type === 'agent_final_response');

    // Also check for stale sessions (> 8 hours since last message)
    const lastMsg = msgs[msgs.length - 1];
    const isStale = lastMsg && (Date.now() - new Date(lastMsg.create_time).getTime() > 8 * 60 * 60 * 1000);

    const needsResume = lastUserIdx >= 0 && !hasAgentFinalAfterLastUser && !isStale;

    // If incomplete, only render messages up to (and including) the last user_query
    const msgsToRender = needsResume ? msgs.slice(0, lastUserIdx + 1) : msgs;

    for (const m of msgsToRender) {
      if (m.message_type === 'user_query') {
        // Flush any orphan tool entries before a new user message
        pendingTools = [];
        markFollowUpSelection(displayMsgs, m.follow_up_id);
        displayMsgs.push({
          id: m.message_id,
          role: 'user',
          content: m.message_content,
          timestamp: new Date(m.create_time),
        });
      } else if (m.message_type === 'tool_use') {
        pendingTools.push(m);
      } else if (m.message_type === 'agent_final_response') {
        // Build thought string from accumulated tool_use messages
        let thought: string | undefined;
        if (pendingTools.length > 0) {
          thought = pendingTools
            .map(t => {
              const body = buildToolStartBody(t.message_content, t.tool_input);
              return `[TOOL_START]${body}[/TOOL]\n[TOOL_DONE]${t.message_content}|success[/TOOL]`;
            })
            .join('\n');
          pendingTools = [];
        }
        // Compute elapsed from the preceding user_query's create_time
        const lastUser = [...displayMsgs].reverse().find(dm => dm.role === 'user');
        const assistantTime = new Date(m.create_time).getTime();
        const userTime = lastUser ? lastUser.timestamp.getTime() : 0;
        const historyElapsedS = lastUser ? (assistantTime - userTime) / 1000 : undefined;
        displayMsgs.push({
          id: m.message_id,
          role: 'assistant',
          content: m.message_content,
          thought,
          timestamp: new Date(m.create_time),
          elapsedS: historyElapsedS && historyElapsedS > 0 ? Math.round(historyElapsedS * 100) / 100 : undefined,
        });
      } else if (m.message_type === 'follow_up_suggestion') {
        attachFollowUpRow(displayMsgs, m);
      }
    }

    setMessages(displayMsgs.length > 0 ? displayMsgs : [{
      id: 'empty-history',
      role: 'assistant' as const,
      content: t('messages.empty'),
      timestamp: new Date(),
    }]);

    setShowHistory(false);

    // If the session is incomplete, resume SSE to get the rest of the agent's response
    if (needsResume) {
      setIsLoading(true);
      const lastUserMsg = msgs[lastUserIdx];
      resumeSession(historicalSessionId, lastUserMsg.conversation_id);
    }
  }, [stopStreamFlush, resumeSession, t]);

  // ── Load older messages on scroll-to-top ──────────────────────────
  const loadingOlderRef = useRef(false);
  const olderMsgGenRef = useRef(0);

  const loadOlderMessages = useCallback(async () => {
    const token = messagesNextTokenRef.current;
    const sid = historySessionIdRef.current;
    if (!token || !sid || loadingOlderRef.current) return;

    loadingOlderRef.current = true;
    setLoadingOlderMessages(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    const gen = ++olderMsgGenRef.current;

    try {
      const resp = await gatewayClient.listSessionMessages(sid, {
        limit: 100,
        order: 'desc',
        next_token: token,
        // Same ownership rule as ChatHistory: admin view needs ?user= (DAT-9051).
        ...(isImpersonating ? { user: effectiveUid } : {}),
      });

      if (gen !== olderMsgGenRef.current) return;

      messagesNextTokenRef.current = resp.next_token;

      const olderMsgs = resp.messages.reverse();
      const olderDisplay: DisplayMessage[] = [];
      let pendingTools: SessionMessage[] = [];

      for (const m of olderMsgs) {
        if (m.message_type === 'user_query') {
          pendingTools = [];
          markFollowUpSelection(olderDisplay, m.follow_up_id);
          olderDisplay.push({
            id: m.message_id,
            role: 'user',
            content: m.message_content,
            timestamp: new Date(m.create_time),
          });
        } else if (m.message_type === 'tool_use') {
          pendingTools.push(m);
        } else if (m.message_type === 'agent_final_response') {
          let thought: string | undefined;
          if (pendingTools.length > 0) {
            thought = pendingTools
              .map(t => `[TOOL_START]${t.message_content}[/TOOL]\n[TOOL_DONE]${t.message_content}|success[/TOOL]`)
              .join('\n');
            pendingTools = [];
          }
          olderDisplay.push({
            id: m.message_id,
            role: 'assistant',
            content: m.message_content,
            thought,
            timestamp: new Date(m.create_time),
          });
        } else if (m.message_type === 'follow_up_suggestion') {
          attachFollowUpRow(olderDisplay, m);
        }
      }

      if (olderDisplay.length > 0) {
        setMessages(prev => [...olderDisplay, ...prev]);
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
          }
        });
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlderMessages(false);
    }
  }, [isImpersonating, effectiveUid]);

  const handleMessagesScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollTop < 50 && messagesNextTokenRef.current && !loadingOlderRef.current) {
      loadOlderMessages();
    }
    if (isProgrammaticScrollRef.current) {
      lastScrollTopRef.current = target.scrollTop;
      return;
    }
    const delta = target.scrollTop - lastScrollTopRef.current;
    lastScrollTopRef.current = target.scrollTop;
    // Ignore sub-pixel jitter (< 2px)
    if (Math.abs(delta) < 2) return;
    if (delta < 0 && !userScrolledUpRef.current) {
      userScrolledUpRef.current = true;
      setUserScrolledUp(true);
    } else if (delta > 0) {
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceFromBottom < 30) {
        userScrolledUpRef.current = false;
        setUserScrolledUp(false);
      }
    }
  }, [loadOlderMessages]);

  const scrollToBottom = useCallback(() => {
    userScrolledUpRef.current = false;
    setUserScrolledUp(false);
    const container = messagesContainerRef.current;
    if (container) {
      isProgrammaticScrollRef.current = true;
      container.scrollTop = container.scrollHeight;
      lastScrollTopRef.current = container.scrollTop;
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    }
  }, []);

  const userInitial = (display_name || uid || 'U').charAt(0).toUpperCase();

  // ── Navigate to a file output in workspace ────────────────────────
  // Delegates to the shared useOpenWorkspaceFile hook: it handles the on/off-
  // workspace-surface routing AND the ivthub-only fallback (users lacking
  // rocky-user/rocky-admin get a clear "request access" message instead of
  // hitting the AccessGate denial screen).
  const handleFileOutputClick = openWorkspaceFile;

  // ── Collapsed state ────────────────────────────────────────────────

  if (isCollapsed) {
    return (
      <aside className="chat-panel chat-panel--collapsed" onClick={onToggle}>
        <button
          className="chat-panel__expand-btn"
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          title={t('header.open')}
          aria-label={t('header.open')}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4H24V20H8L4 24V4Z" stroke="#424650" strokeWidth="2" strokeLinejoin="round" fill="none"/>
          </svg>
        </button>
      </aside>
    );
  }

  // ── Determine streaming phase ─────────────────────────────────────
  // text_delta has started arriving (final response text is being streamed)
  const hasFinalStarted = isLoading && streamingText.length > 0;

  // Avatar stays in loading state for the entire streaming duration —
  // only stops loading once the stream is fully complete (isLoading → false).
  const avatarLoading = isLoading;

  // ── Render ────────────────────────────────────────────────────────

  return (
    <aside
      className={`chat-panel${chatFullscreen ? ' chat-panel--fullscreen' : ''}`}
      style={chatFullscreen ? undefined : { width: chatWidth, minWidth: 280, maxWidth: 700 }}
    >
      {/* Drag handle on left edge (hidden in fullscreen) */}
      {!chatFullscreen && <div className="chat-panel__resize-handle" onMouseDown={handleResizeStart} />}

      {/* Overlay during resize */}
      {isResizing && <div className="chat-panel__resize-overlay" />}

      {/* Header */}
      <div className="chat-panel__header">
        <button className="chat-panel__header-btn chat-panel__header-btn--lead" onClick={() => setShowHistory(h => !h)} title={showHistory ? t('header.backToChat') : t('header.history')} aria-label={showHistory ? t('header.backToChat') : t('header.history')}>
          {showHistory ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="#424650" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          ) : (
            <img src="/icons/icon-chat-list.svg" alt="" />
          )}
        </button>
        <h2 className="chat-panel__title">{showHistory ? t('header.historyTitle') : t('header.title')}</h2>
        <div className="chat-panel__header-actions">
          <button className="chat-panel__header-btn" onClick={handleNewSession} title={t('header.newConversation')} aria-label={t('header.newConversation')}>
            <img src="/icons/icon-chat-add.svg" alt="" />
          </button>
          {!hideFullscreen && (
            <button className="chat-panel__header-btn" onClick={toggleFullscreen} title={chatFullscreen ? t('header.exitFullscreen') : t('header.enterFullscreen')} aria-label={chatFullscreen ? t('header.exitFullscreen') : t('header.enterFullscreen')}>
              <img src={chatFullscreen ? "/icons/chat-half-screen.svg" : "/icons/chat-full-screen.svg"} alt="" />
            </button>
          )}
          {onToggle && (
            <button className="chat-panel__header-btn" onClick={onToggle} title={t('header.close')} aria-label={t('header.close')}>
              <img src="/icons/icon-chat-close.svg" alt="" />
            </button>
          )}
        </div>
      </div>

      {/* Phones only — names the attached Workspace file, which the open chat hides. */}
      {!showHistory && (
        <ContextFileBanner
          fileName={contextFile?.name}
          enabled={contextFileEnabled}
          onExclude={() => setContextFileEnabled(false)}
        />
      )}

      {/* Messages / History */}
      {showHistory ? (
        <ChatHistory
          onLoadSession={handleLoadSession}
          agentName={agentName}
        />
      ) : (
      <div className="chat-panel__messages-wrap">
        <div className="chat-panel__messages" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
          {loadingOlderMessages && (
            <div className="chat-panel__loading-older">{t('messages.loadingOlder')}</div>
          )}
          {messages.map((msg, msgIndex) => {
            if (msg.role === 'divider') {
              return <div key={msg.id} className="chat-msg--divider">{msg.content}</div>;
            }

            if (msg.role === 'user') {
              const { text: userText, attachedFiles } = parseUserContent(msg.content);
              return (
                <div key={msg.id} className="chat-msg--user">
                  <Avatar initials={userInitial} color="default" ariaLabel={t('messages.userAvatar', { name: display_name || uid || t('messages.userFallback') })} />
                  <div className="chat-msg__user-text">
                    {userText}
                    {attachedFiles.map((fname, i) => (
                      <span key={i} className="chat-msg__context-file-label">
                        <Icon name="file" size="small" /> {fname}
                      </span>
                    ))}
                    {msg.contextFileName && (
                      <span className="chat-msg__context-file-label">
                        <Icon name="file" size="small" /> {msg.contextFileName}
                      </span>
                    )}
                    {msg.assetScopeName && (
                      <span className="chat-msg__context-file-label">
                        <Icon name="gen-ai" size="small" /> KB: {msg.assetScopeName}
                      </span>
                    )}
                    {msg.ucScopeName && (
                      <span className="chat-msg__context-file-label">
                        <Icon name="gen-ai" size="small" /> KB: {msg.ucScopeName}
                      </span>
                    )}
                    {msg.storylineScopeName && (
                      <span className="chat-msg__context-file-label">
                        <Icon name="gen-ai" size="small" /> KB: {msg.storylineScopeName}
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            // assistant (completed)
            return (
              <div key={msg.id} className="chat-msg--ai">
                <div className="chat-msg__ai-avatar-wrap">
                  <Avatar imgUrl={assistantAvatarUrl} color="gen-ai" ariaLabel={isRocky ? t('messages.assistantAvatar') : t('messages.genericAssistantAvatar')} />
                </div>
                <div className="chat-msg__ai-content">
                  {msg.thought && (
                    <ThoughtBlock thought={msg.thought} isStreaming={false} onFileClick={handleFileOutputClick} />
                  )}
                  <div className="chat-msg__bubble">
                    <div className="chat-msg__bubble-text">
                      {/* The welcome is rendered in the current locale, not as stored at session start. */}
                      <MarkdownRenderer markdown={msg.id === 'welcome' ? getAgentWelcome(agentName, t) : msg.content} />
                    </div>
                  </div>
                  {msg.fileOutputs && msg.fileOutputs.length > 0 && (
                    <FileOutputCards files={msg.fileOutputs} onFileClick={handleFileOutputClick} />
                  )}
                  {msg.elapsedS != null && (() => {
                    if (msg.elapsedS < 60) return (
                      <div className="chat-msg__timing">
                        <span style={{fontSize: '15px'}}>⏱</span> {msg.elapsedS.toFixed(1)}s
                      </div>
                    );
                    const totalSec = Math.round(msg.elapsedS);
                    return (
                      <div className="chat-msg__timing">
                        <span style={{fontSize: '15px'}}>⏱</span> {Math.floor(totalSec / 60)}m {totalSec % 60}s
                      </div>
                    );
                  })()}
                  {msg.followUps && (
                    <ChoiceGroup
                      component={msg.followUps.component}
                      label={t('followUps.title')}
                      variant="follow-up"
                      selectedId={msg.followUps.selectedId}
                      disabled={!isFollowUpInteractive(msgIndex, messages.length, isLoading, msg.followUps)}
                      onSubmit={option => handleFollowUpClick(msg.id, option)}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Live streaming bubble */}
          {isLoading && (
            <div className="chat-msg--ai">
              <div className="chat-msg__ai-avatar-wrap">
                <Avatar imgUrl={assistantAvatarUrl} color="gen-ai" ariaLabel={isRocky ? t('messages.assistantAvatar') : t('messages.genericAssistantAvatar')} loading={avatarLoading} />
              </div>
              <div className="chat-msg__ai-content">
                {/* Thinking block — always visible during streaming, replaces "Thinking..." text */}
                <ThoughtBlock thought={streamingThought || ''} isStreaming={true} onFileClick={handleFileOutputClick} />

                {/* Response bubble — only when text_delta has started */}
                {hasFinalStarted && (
                  <div className="chat-msg__bubble">
                    <div className="chat-msg__bubble-text">
                      <MarkdownRenderer markdown={streamingText} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Suggested questions — inside chat, below last AI message. */}
          {!isLoading && !hasChatted && !isImpersonating && (() => {
            if (showDefaultSuggestions) {
              return <SuggestedQuestions questions={defaultSuggestedQuestions.map((q) => q[locale])} onSelect={(q) => handleSend(q)} />;
            }
            const reportQuestions = metadata?.suggested_questions?.length ? metadata.suggested_questions : null;
            if (reportQuestions) {
              return <SuggestedQuestions questions={reportQuestions} onSelect={(q) => handleSend(q)} />;
            }
            return null;
          })()}

          <div ref={bottomRef} />
        </div>

        {/* Floating "scroll to latest" button — positioned over messages area */}
        {userScrolledUp && (
          <button
            className="chat-panel__scroll-to-bottom"
            onClick={scrollToBottom}
            title={t('messages.scrollToLatest')}
            aria-label={t('messages.scrollToLatest')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M8 13L3 8M8 13L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      )}

      {/* AI disclaimer — pinned at the bottom of the conversation/answer area,
          just above the composer, in line with ChatGPT-style assistant UX.
          Links to an explainer page on why responses can be inaccurate. */}
      <RouterLink
        className="chat-panel__disclaimer"
        to="/help/ai-responses"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('disclaimer')}
      </RouterLink>

      {/* Input area */}
      <div className="chat-panel__input-area">
        {queueToast && (
          <div className="chat-panel__queue-toast" role="status" aria-live="polite">
            {queueToast}
          </div>
        )}
        <PendingQueue
          items={pendingQueue}
          onCancel={handleCancelQueued}
          onSendNow={handleSendQueuedNow}
        />
        <div className="chat-panel__input-wrapper" style={inputHeight ? { height: inputHeight } : undefined} onClick={() => textareaRef.current?.focus()}>
          <div className="chat-panel__input-resize-handle" onMouseDown={handleInputResizeStart} />
          {/* Drag-and-drop overlay — only mounts while a file is being dragged
              over the window and uploads are allowed. Scoped to the input box
              (not the whole panel) so it never blocks normal clicks. Reuses the
              same processFiles path as the Attach Files picker. */}
          {areFilesDragging && !isLoading && !isUploading && !isImpersonating && (
            <div
              className="chat-panel__dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFileDrop(Array.from(e.dataTransfer.files));
              }}
            >
              <div className="chat-panel__dropzone-content">
                <svg className="chat-panel__dropzone-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 15V4M12 4L8 8M12 4l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 15v2.5A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5V15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="chat-panel__dropzone-text">{t('files.drop')}</span>
              </div>
            </div>
          )}
          {/* File attachment cards (Cloudscape-style) */}
          {pendingFiles.length > 0 && (
            <div className="chat-panel__file-cards">
              {pendingFiles.map(pf => (
                <div key={pf.id} className={`chat-panel__file-card ${pf.status === 'error' ? 'chat-panel__file-card--error' : ''}`}>
                  <div className="chat-panel__file-card-info">
                    <span className="chat-panel__file-card-name" title={pf.file.name}>
                      {pf.file.name}
                    </span>
                    <span className="chat-panel__file-card-meta">
                      {pf.file.size < 1024
                        ? `${pf.file.size} B`
                        : pf.file.size < 1048576
                          ? `${(pf.file.size / 1024).toFixed(1)} KB`
                          : `${(pf.file.size / 1048576).toFixed(1)} MB`}
                    </span>
                    <span className="chat-panel__file-card-meta">
                      {new Date(pf.file.lastModified).toISOString().slice(0, 19)}
                    </span>
                    {pf.status === 'uploading' && (
                      <span className="chat-panel__file-card-status">{t('files.uploading')}</span>
                    )}
                  </div>
                  <button
                    className="chat-panel__file-card-dismiss"
                    onClick={() => handleRemoveFile(pf.id)}
                    title={t('files.remove')}
                    aria-label={t('files.removeNamed', { name: pf.file.name })}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {isImpersonating && (
            <span className="chat-panel__lock-icon">
              <Icon name="lock-private" size="small" />
            </span>
          )}
          <textarea
            ref={textareaRef}
            className={`chat-panel__textarea ${isImpersonating ? 'chat-panel__textarea--locked' : ''}`}
            style={inputHeight ? { height: '100%', maxHeight: 'none' } : undefined}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={isImpersonating ? t('input.readOnly') : isLoading ? (pendingQueue.length === 0 ? t('input.emailFallback') : t('input.willQueue')) : t('input.placeholder')}
            disabled={isImpersonating}
            rows={1}
          />
          <div className="chat-panel__input-actions">
            {isLoading && (
              <button
                className="chat-panel__input-btn chat-panel__stop-btn"
                onClick={handleStop}
                title={pendingQueue.length > 0 ? t('input.stopKeepQueue') : t('input.stop')}
                aria-label={t('input.stop')}
              >
                <img src="/icons/stop-circle.svg" alt="" />
              </button>
            )}
            {contextPercent > 0 && (
              <div className="chat-panel__ctx-ring" title={t('input.context', { percent: contextPercent.toFixed(1) })}>
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="#e8e8ed" strokeWidth="2.5" />
                  <circle
                    cx="12" cy="12" r="10" fill="none"
                    stroke={contextPercent < 25 ? '#037f0c' : contextPercent < 75 ? '#b8860b' : '#d91515'}
                    strokeWidth="2.5"
                    strokeDasharray={`${(contextPercent / 100) * 62.83} 62.83`}
                    strokeLinecap="round"
                    transform="rotate(-90 12 12)"
                  />
                </svg>
              </div>
            )}
            <button
              className="chat-panel__input-btn"
              onClick={() => handleSend()}
              disabled={(!inputText.trim() && !(isLoading && pendingQueue.length === 0)) || isUploading || isImpersonating}
              title={isLoading ? t('input.addToQueue') : t('input.send')}
              aria-label={isLoading ? t('input.addToQueue') : t('input.send')}
            >
              <img src="/icons/icon-chat-send.svg" alt="" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="chat-panel__toolbar">
          <div className="chat-panel__toolbar-left">
            {/* Rocky's Pack — Rocky-only skill row; hidden for other agents. */}
            {isRocky && (
            <div className="chat-panel__toolbar-pack">
              {groupedSkills.has('DataRetriever') ? (
                <SkillCard
                  category="DataRetriever"
                  skills={groupedSkills.get('DataRetriever')!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ) : (
                <span className="chat-panel__pack-avatar-frame chat-panel__pack-avatar-frame--disabled">
                  <Avatar imgUrl="/icons/avatar-pack-dog.png" width={26} color="default" ariaLabel="DataRetriever" />
                </span>
              )}
              {groupedSkills.has('Hedwig') ? (
                <SkillCard
                  category="Hedwig"
                  skills={groupedSkills.get('Hedwig')!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ) : (
                <span className="chat-panel__pack-avatar-frame chat-panel__pack-avatar-frame--disabled">
                  <Avatar imgUrl="/icons/avatar-pack-owl.png" width={26} color="default" ariaLabel="Hedwig" />
                </span>
              )}
              {groupedSkills.has('Cheetah') ? (
                <SkillCard
                  category="Cheetah"
                  skills={groupedSkills.get('Cheetah')!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ) : (
                <span className="chat-panel__pack-avatar-frame chat-panel__pack-avatar-frame--disabled">
                  <Avatar imgUrl="/icons/cheetah.png" width={26} color="default" ariaLabel="Cheetah" />
                </span>
              )}
              {groupedSkills.has('IVT') ? (
                <SkillCard
                  category="IVT"
                  skills={groupedSkills.get('IVT')!}
                  onSelectSkill={handleSkillSelect}
                  disabled={isLoading || isImpersonating}
                />
              ) : (
                <span className="chat-panel__pack-avatar-frame chat-panel__pack-avatar-frame--disabled">
                  <Avatar imgUrl="/icons/ivt.png" width={26} color="default" ariaLabel="IVT" />
                </span>
              )}
              <span className="chat-panel__toolbar-label">{t('pack.label')}</span>
            </div>
            )}
            {contextFile && (
              <>
                {/* Leading divider only when the pack precedes this chip. */}
                {isRocky && <div className="chat-panel__toolbar-divider" />}
                <button
                  className={`chat-panel__toolbar-context-file ${!contextFileEnabled ? 'chat-panel__toolbar-context-file--disabled' : ''}`}
                  onClick={() => setContextFileEnabled(prev => !prev)}
                  title={contextFileEnabled ? t('contextFile.chipExcludeTitle', { name: contextFile.name }) : t('contextFile.chipIncludeTitle', { name: contextFile.name })}
                  aria-label={contextFileEnabled ? t('contextFile.chipExclude', { name: contextFile.name }) : t('contextFile.chipInclude', { name: contextFile.name })}
                >
                  <Icon
                    name={contextFileEnabled ? 'file' : undefined}
                    variant={contextFileEnabled ? 'normal' : 'disabled'}
                    size="small"
                    svg={!contextFileEnabled ? (
                      <svg viewBox="0 0 16 16" focusable="false">
                        <path d="M2 8s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="8" cy="8" r="1.5" fill="none" stroke="currentColor"/>
                        <path d="M3 13L13 3" stroke="currentColor" strokeLinecap="round"/>
                      </svg>
                    ) : undefined}
                  />
                  <span className="chat-panel__toolbar-context-file-name">{contextFile.name}</span>
                </button>
              </>
            )}
            {assetScope && (
              <>
                {(isRocky || !!contextFile) && <div className="chat-panel__toolbar-divider" />}
                <button
                  className="chat-panel__toolbar-asset-scope"
                  onClick={() => setAssetScope(null)}
                  title={t('scope.assetTitle', { name: assetScope.assetName })}
                  aria-label={t('scope.assetExit', { name: assetScope.assetName })}
                >
                  <Icon name="gen-ai" size="small" />
                  <span className="chat-panel__toolbar-asset-scope-name">
                    {t('scope.kb', { name: assetScope.assetName })}
                  </span>
                  <Icon name="close" size="small" />
                </button>
              </>
            )}
            {/* UC scope chip — parallel to the asset chip; asset/UC/storyline are
                mutually exclusive so at most one renders. Reuses the same toolbar styling. */}
            {ucScope && (
              <>
                {(isRocky || !!contextFile || !!assetScope) && <div className="chat-panel__toolbar-divider" />}
                <button
                  className="chat-panel__toolbar-asset-scope"
                  onClick={() => setUcScope(null)}
                  title={t('scope.useCaseTitle', { name: ucScope.ucName })}
                  aria-label={t('scope.useCaseExit', { name: ucScope.ucName })}
                >
                  <Icon name="gen-ai" size="small" />
                  <span className="chat-panel__toolbar-asset-scope-name">
                    {t('scope.kb', { name: ucScope.ucName })}
                  </span>
                  <Icon name="close" size="small" />
                </button>
              </>
            )}
            {/* Storyline scope chip — parallel to the asset/UC chips. */}
            {storylineScope && (
              <>
                {(isRocky || !!contextFile || !!assetScope || !!ucScope) && <div className="chat-panel__toolbar-divider" />}
                <button
                  className="chat-panel__toolbar-asset-scope"
                  onClick={() => setStorylineScope(null)}
                  title={t('scope.storylineTitle', { name: storylineScope.storylineName })}
                  aria-label={t('scope.storylineExit', { name: storylineScope.storylineName })}
                >
                  <Icon name="gen-ai" size="small" />
                  <span className="chat-panel__toolbar-asset-scope-name">
                    {t('scope.kb', { name: storylineScope.storylineName })}
                  </span>
                  <Icon name="close" size="small" />
                </button>
              </>
            )}
          </div>
          <ModelSelect
            choices={modelChoice.choices}
            selected={modelChoice.selected}
            onSelect={modelChoice.select}
            disabled={isImpersonating}
          />
          <button
            className="chat-panel__toolbar-attach"
            title={t('files.attach')}
            aria-label={t('files.attach')}
            onClick={handleAttachClick}
            disabled={isLoading || isUploading || isImpersonating}
          >
            <img src="/icons/icon-chat-upload.svg" alt="" />
            <span>{isUploading ? t('files.uploading') : t('files.attachLabel')}</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept={getAllowedUploadExts().join(',')}
          />
        </div>

      </div>
    </aside>
  );
}
